import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  Modal,
  TextField,
  Typography
} from '@mui/material';

import LoadingButton from '../../../../../components/buttons/Button';
import useSearchableSelect from '../../../../../components/hooks/useSearchableSelect';

import {
  CreateUstrExclusionBIZcoreTD,
  UpdateUstrExclusionBIZcoreTD
} from '../../../../../redux/actions/business/core/tradeDefencesActions';
import { GetUsitcHtsCodesDropdownBIZcoreTD } from '../../../../../redux/actions/business/core/tradeDefencesActions';

import {
  CLEAR_USITC_HTS_CODES_DROPDOWN_BIZ_CORE_TD
} from '../../../../../redux/actionTypes';

import global from "../../../../../global.scss";

const MODES = { CREATE: 1, EDIT: 2 };

const Data = {
  UsitcHts: null,
  Chapter99Hts: "",
  ExclusionType: "",
  PenaltyRate: "",
  ActiveDate: "",
  ExpirationDate: "",
};

const UstrExclusionsModal = ({
  open,
  setOpen,
  title,
  update,
  handleGet
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(Data);
  const [mode, setMode] = useState(MODES.CREATE);

  // ── Searchable select: USITC HTS Codes ───────────────────────────────────
  const {
    results: htsResults,
    loading: htsSearchLoading,
    handleSearch: handleHtsSearch,
    reset: resetHtsSearch,
  } = useSearchableSelect(
    GetUsitcHtsCodesDropdownBIZcoreTD,
    (state) => {
      const list = state["business.core.tradeDefence"].usitcHtsCodesDropdownList;
      if (list?.records && Array.isArray(list.records)) {
        return list.records;
      }
      return [];
    },
    null,
    { limit: 10, formatQuery: (q) => `&search=${q}` }
  );

  const handleClose = () => {
    setOpen(false);
    setFormData(Data);
    setMode(MODES.CREATE);
    dispatch({ type: CLEAR_USITC_HTS_CODES_DROPDOWN_BIZ_CORE_TD });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    try {
      if (open) {
        resetHtsSearch();
        if (update.defaultMode === MODES.CREATE) {
          setMode(MODES.CREATE);
        } else if (update.defaultMode === MODES.EDIT) {
          setMode(MODES.EDIT);
          setFormData({
            UsitcHts: update.ref_usitc_hts_id || null,
            Chapter99Hts: update.chapter_99_hts || "",
            ExclusionType: update.exclusion_type || "",
            PenaltyRate: update.penalty_rate ?? "",
            ActiveDate: update.active_date || "",
            ExpirationDate: update.expiration_date || "",
          });
        } else {
          toast.error("Invalid modal");
          handleClose();
        }
      }
    } catch (err) {
      console.log(err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = () => {
    const obj = {
      ref_usitc_hts_id: formData.UsitcHts,
      chapter_99_hts: formData.Chapter99Hts || null,
      exclusion_type: formData.ExclusionType || null,
      penalty_rate: formData.PenaltyRate !== "" ? formData.PenaltyRate : null,
      active_date: formData.ActiveDate,
      expiration_date: formData.ExpirationDate || null,
    };

    if (mode === MODES.CREATE) {
      dispatch(CreateUstrExclusionBIZcoreTD(obj, setLoading)).then(() => {
        handleGet(setLoading).then(() => {
          handleClose();
        });
      });
    } else if (mode === MODES.EDIT) {
      dispatch(UpdateUstrExclusionBIZcoreTD(update.id, obj, setLoading)).then(() => {
        handleGet(setLoading).then(() => {
          handleClose();
        });
      });
    } else {
      toast.error("Invalid modal");
      handleClose();
    }
  };

  const isSubmitDisabled = () => {
    return !(formData.UsitcHts && formData.ActiveDate !== "");
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
    >
      <Box className="flexCenterCenterRow" width={"100vw"} height={"100vh"}>
        <Box maxWidth={"sm"} className="modal">
          <Box width={"100%"} sx={{ padding: "15px" }}>
            <Typography variant='h6'
              sx={{ color: global["primary"] }} mb={"20px"}>
              {title}
            </Typography>

            {/* USITC HTS Code */}
            <Box mb={"15px"}>
              <Autocomplete
                fullWidth
                options={Array.isArray(htsResults) ? htsResults : []}
                loading={htsSearchLoading}
                disabled={mode === MODES.EDIT}
                value={
                  formData.UsitcHts
                  ? (Array.isArray(htsResults) ? htsResults : []).find(h => h.id === formData.UsitcHts) || formData.UsitcHts
                  : null
                }
                onInputChange={(e, value) => handleHtsSearch(value)}
                onChange={(e, value) => setFormData({ ...formData, UsitcHts: value?.id || null })}
                getOptionLabel={(option) =>
                  typeof option === 'string'
                  ? option
                  : (option.hts_code || option.id || "NA")
                }
                isOptionEqualToValue={(option, value) => {
                  if (typeof value === 'string') return option.id === value;
                  return option.id === value?.id;
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2">{option.hts_code || option.id || "NA"}</Typography>
                      {option.description && (
                        <Typography variant="caption" sx={{ color: "grey" }}>
                          {option.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="USITC HTS code"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {htsSearchLoading ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>

            {/* Active Date + Expiration Date */}
            <Box className="flexCenterSBRow" gap={"15px"} mb={"15px"}>
              <TextField
                label="Active date"
                fullWidth
                name="ActiveDate"
                type="date"
                value={formData.ActiveDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Expiration date (optional)"
                fullWidth
                name="ExpirationDate"
                type="date"
                value={formData.ExpirationDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            {/* Chapter 99 HTS + Exclusion Type */}
            <Box className="flexCenterSBRow" gap={"15px"} mb={"15px"}>
              <TextField
                label="Chapter 99 HTS (optional)"
                fullWidth
                name="Chapter99Hts"
                value={formData.Chapter99Hts}
                onChange={handleChange}
                inputProps={{ maxLength: 20 }}
              />
              <TextField
                label="Exclusion type (optional)"
                fullWidth
                name="ExclusionType"
                value={formData.ExclusionType}
                onChange={handleChange}
                inputProps={{ maxLength: 50 }}
              />
            </Box>

            {/* Penalty Rate */}
            <Box mb={"15px"}>
              <TextField
                label="Penalty rate (optional)"
                fullWidth
                name="PenaltyRate"
                type="number"
                value={formData.PenaltyRate}
                onChange={handleChange}
                inputProps={{ min: 0, step: "0.0001" }}
              />
            </Box>
          </Box>

          <Divider sx={{ backgroundColor: "rgba(44, 45, 47, 1)" }} />
          <Box className="flexCenterFERow" sx={{ gap: "20px", margin: "15px" }}>
            <Button variant='text' onClick={handleClose}>
              Cancel
            </Button>
            <LoadingButton
              variant='contained'
              loading={loading}
              disabled={isSubmitDisabled()}
              onClick={handleSubmit}
            >
              Submit
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default UstrExclusionsModal;
